import { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import { BASE } from 'design-system/variables/spacing';
import ImageDesktop from 'images/pictographs/desktop.svg';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import UiComponentView from 'inquiry/components/UiComponentView';
import { StepCompleteConfig } from 'lib/interfaces/StepsConfig';
import { ImageKey, UiComponentType } from 'lib/interfaces/ui-components';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import ThemedSvg from 'verify/components/ThemedSvg';

const Header = styled('div')`
  margin-bottom: ${BASE * 8}px;
`;

const InquiryCompleteView = ({
  onComplete,
  hasCompleteAction,
  isChildFlow,
  config,
}: {
  onComplete: () => void;
  hasCompleteAction: boolean;
  isChildFlow: boolean;
  config: StepCompleteConfig;
}): ReactElement => {
  const [t] = useTranslation();

  const shouldAutoClose = hasCompleteAction && !isChildFlow;

  useEffect(() => {
    if (shouldAutoClose) {
      onComplete();
    }
  }, []);

  if (shouldAutoClose) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return null;
  }

  return isChildFlow ? (
    <div data-test='inquiry-complete-view'>
      <Header>
        <ThemedTitle>
          {config.localizations?.['child-flow-page']?.title ?? t('success.title-exchange-flow')}
        </ThemedTitle>
        <ThemedParagraph>
          <Markdown>
            {config.localizations?.['child-flow-page']?.prompt ?? t('success.prompt-exchange-flow')}
          </Markdown>
        </ThemedParagraph>
      </Header>

      <CenteredPictograph aria-label='return to original device pictograph'>
        <ThemedSvg vectorEffect='initial'>
          <ImageDesktop />
        </ThemedSvg>
      </CenteredPictograph>
    </div>
  ) : (
    // fallback to the success view when hosted flow does not have a redirect URI
    <div data-test='inquiry-complete-view'>
      <UiComponentView
        component={{
          name: 'success_animated_checkmark',
          type: UiComponentType.ImageLocal,
          attributes: { 'image-key': ImageKey.AnimatedCheck },
        }}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        inquiry={null}
        inquiryLoading={false}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        actionsApi={null}
      />
    </div>
  );
};

export default InquiryCompleteView;
